import { Component, ChangeDetectorRef, OnInit, ViewChildren, QueryList, TrackByFunction } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarRef as MatSnackBarRef,
  LegacySimpleSnackBar as SimpleSnackBar,
} from '@angular/material/legacy-snack-bar';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { Account, AccountColor, AccountSettings } from 'app/core/models';
import { FileFormat } from 'app/shared/enums';
import { AccountSubdomain, FeatureFlag, NewPermission } from 'app/core/enums';
import { generateDownloadableFile } from 'app/shared/functions';
import { ReportsService } from 'app/modules/reports/reports.service';
import { ColorPickerDirective } from 'ngx-color-picker';
import { TsSnackbarService } from 'tsui';
import { EMPTY, noop, Observable, tap } from 'rxjs';
import { Actions, ofActionErrored, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { AccountActions } from '@state/account/actions';
import { take, withLatestFrom } from 'rxjs/operators';
import { environment } from '@env/environment';
import { UserState } from '@state/user/state';

function uniqueColorNameValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!(control instanceof FormArray)) {
      return null;
    }

    let list = (control as FormArray).controls;

    let duplicates = list
      .map((formGroup) => formGroup.value)
      .map((value) => value.label?.toLowerCase())
      .some((value, index, array) => array.indexOf(value) !== array.lastIndexOf(value));

    return duplicates ? { duplicateEntries: true } : null;
  };
}

@Component({
  selector: 'general-settings',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
})
export class GeneralSettingsComponent extends OnDestroyMixin implements OnInit {
  currentVersion: string;
  enabledFeatureFlags: string[];
  onlyPostalAllowed: boolean;
  settingsForm: UntypedFormGroup;
  protected readonly NewPermission = NewPermission;
  allFeatureFlags: FeatureFlag[] = [];

  @ViewChildren(ColorPickerDirective) colorPickers: QueryList<ColorPickerDirective>;

  @Select(AccountState.getAccount) account$: Observable<Account>;

  trackByFn: TrackByFunction<any> = (index) => index;

  constructor(
    private readonly accountService: AccountService,
    private readonly reportsService: ReportsService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly typedFormBuilder: FormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar,
    private readonly tsSnackBarService: TsSnackbarService,
    private readonly store: Store,
    private readonly actions: Actions,
  ) {
    super();
    this.account$.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this.reportsService.accountId = accountData.id;
    });
  }

  get colorsFormArray(): UntypedFormArray {
    return this.settingsForm.get('colors') as FormArray;
  }

  get colorsFormArrayControls() {
    return this.colorsFormArray.controls as FormGroup[];
  }

  ngOnInit(): void {
    this.settingsForm = this.formBuilder.group({
      id: [''],
      name: [''],
      subdomain: [''],
      display_currency: [''],
      configuration: this.formBuilder.group({
        show_images_on_pipeline: [''],
        merge_all_comments_within_object: [''],
      }),
      colors: this.formBuilder.array([], uniqueColorNameValidator()),
    });

    this.enabledFeatureFlags = [];
    // SC-37329 - This will get replaced by the current version as part of the build process.
    this.currentVersion = environment.frontEndCodeVersion;

    // Set the defaults
    this.account$
      .pipe(
        take(1),
        tap((account) => {
          this.onlyPostalAllowed = account.subdomain.includes(AccountSubdomain.POSTAL);
          this.settingsForm.patchValue(account);
          this.settingsForm.get('configuration').patchValue(account);
          account?.colors.forEach((color) => {
            this.colorsFormArray.push(this.createAccountColorForm(color));
          });
          account?.features.sort().forEach((featureFlag) => {
            this.enabledFeatureFlags.push(featureFlag);
          });
        }),
      )
      .subscribe(noop);

    this.accountService.getFeatureFlags().subscribe((featureFlags) => {
      this.allFeatureFlags = featureFlags;
      this.changeDetectorRef.markForCheck();
    });

    this.loadActionListeners();
    this.changeDetectorRef.markForCheck();
  }

  updateSettings(): void {
    if (this.settingsForm.invalid) return;

    const settings = this.settingsForm.value as AccountSettings;

    this.store.dispatch(new AccountActions.UpdateAccount(settings));
  }

  scheduleExport(objectType: string): void {
    this.accountService
      .scheduleExport(objectType)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.userFeedbackMessage('Export Scheduled. You will receive an email shortly with an attachment.');
      });
  }

  downloadPostalReport(): void {
    this.userFeedbackMessage('Download will begin shortly.');
    this.reportsService
      .downloadPostalReport()
      .pipe(untilComponentDestroyed(this))
      .subscribe(
        (data) => {
          if (data) generateDownloadableFile(data, FileFormat.PDF, `PSTL Acquisition Report`);
        },
        () => {
          this.userFeedbackMessage('Report generation failed. Try again later.');
        },
      );
  }

  private userFeedbackMessage(message: string): MatSnackBarRef<SimpleSnackBar> {
    return this.snackBar.open(message, '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  onImageChange(file: File) {
    this.store.dispatch(new AccountActions.UploadAccountImage(file));
  }

  onColorSelected($event: string, colorControlGroupIndex: number) {
    this.colorsFormArray.at(colorControlGroupIndex).patchValue({ hex_value: $event }, { emitModelToViewChange: true });
    this.settingsForm.markAsDirty();
  }

  openColorPicker(i: number) {
    this.colorPickers.get(i).openDialog();
  }

  addNewColor() {
    this.colorsFormArray.push(this.createAccountColorForm());
  }

  removeColor(colorControlGroupIndex: number) {
    const colorControlGroup = this.colorsFormArray.at(colorControlGroupIndex);

    if (colorControlGroup.value.color_type === 'primary') return;

    // if we have an id, it means we fetched the color from the api; otherwise it was just a local change
    if (colorControlGroup.get('id').value) {
      this.colorsFormArray.at(colorControlGroupIndex).patchValue({ _destroy: true }, { emitModelToViewChange: true });
      this.settingsForm.markAsDirty();
    } else {
      this.colorsFormArray.removeAt(colorControlGroupIndex);
    }
  }

  onSetPrimaryColor(i: number) {
    const currentPrimaryColorControl = this.colorsFormArrayControls.find(
      (controls) => controls.get('color_type').value === 'primary',
    );

    if (currentPrimaryColorControl) {
      currentPrimaryColorControl.patchValue({ color_type: 'none' });
    }

    this.colorsFormArray.at(i).patchValue({ color_type: 'primary' });
    this.colorsFormArray.markAsDirty();
  }

  onImageRemoved() {
    this.store.dispatch(new AccountActions.RemoveAccountImage());
  }

  private loadActionListeners() {
    this.actions
      .pipe(
        ofActionSuccessful(AccountActions.UpdateAccount),
        withLatestFrom(this.account$),
        untilComponentDestroyed(this),
      )
      .subscribe(([, account]) => {
        this.settingsForm.reset(account);
        this.settingsForm.get('configuration').patchValue(account);
        this.colorsFormArray.clear();
        account?.colors.forEach((color) => {
          if (color.id) {
            this.colorsFormArray.push(this.createAccountColorForm(color));
          }
        });

        this.tsSnackBarService.open('Settings saved', 'success');
      });

    this.actions
      .pipe(ofActionErrored(AccountActions.UpdateAccount), untilComponentDestroyed(this))
      .subscribe((error) => {
        this.tsSnackBarService.open('Error saving settings', 'error');
        return EMPTY;
      });

    this.actions
      .pipe(ofActionSuccessful(AccountActions.UploadAccountImage), untilComponentDestroyed(this))
      .subscribe(() => {
        this.tsSnackBarService.open('Logo updated successfully', 'success');
      });

    this.actions
      .pipe(ofActionErrored(AccountActions.UploadAccountImage), untilComponentDestroyed(this))
      .subscribe(() => {
        this.tsSnackBarService.open('Logo upload error', 'error');
      });

    this.actions
      .pipe(ofActionSuccessful(AccountActions.RemoveAccountImage), untilComponentDestroyed(this))
      .subscribe(() => {
        this.tsSnackBarService.open('Logo removed successfully', 'success');
      });

    this.actions
      .pipe(ofActionErrored(AccountActions.RemoveAccountImage), untilComponentDestroyed(this))
      .subscribe(() => {
        this.tsSnackBarService.open('Logo removal error', 'error');
      });
  }

  termsheetUser(): boolean {
    const user = this.store.selectSnapshot(UserState.getUser);
    return environment.production ? user.email.endsWith('@termsheet.com') || user.email.endsWith('@intapp.com') : true;
  }

  toggleFeatureFlag(featureFlagSlug) {
    this.accountService.toggleFeatureFlag(featureFlagSlug).subscribe((featureFlags) => {
      this.allFeatureFlags = featureFlags;
      this.changeDetectorRef.markForCheck();
    });
  }

  private createAccountColorForm(color?: AccountColor): FormGroup<Record<keyof AccountColor, FormControl>> {
    if (color) {
      return this.typedFormBuilder.group({
        id: [color.id],
        name: [color.name],
        label: [color.label, Validators.required],
        hex_value: [color.hex_value],
        color_type: [color.color_type],
        _destroy: [false],
      });
    }

    return this.typedFormBuilder.group({
      id: null,
      name: [''],
      label: ['', Validators.required],
      hex_value: ['#ffffff'],
      color_type: ['none'],
      _destroy: [false],
    });
  }
}
